import React, { useContext, useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CartContext } from "../CartContext";
import { AuthContext } from "../AuthContext";
import Swal from "sweetalert2";
import "./Cart.css";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function Cart() {
  const { cart, addToCart, removeFromCart, clearCart, decreaseQuantity } =
    useContext(CartContext);
  const { isLoggedIn, userInfo } = useContext(AuthContext);
  const [productDetails, setProductDetails] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [cartCompositionError, setCartCompositionError] = useState("");
  const [minimumSpendAmount, setMinimumSpendAmount] = useState(0);
  const [minimumSpendError, setMinimumSpendError] = useState("");
  const [promoMap, setPromoMap] = useState({});
  const navigate = useNavigate();

  const separateCartItems = () => {
    const sellingItems = cart.filter(item => !item.isRedeemable);
    const redeemableItems = cart.filter(item => item.isRedeemable);
    return { sellingItems, redeemableItems };
  };

  const validateCartComposition = () => {
    const { sellingItems, redeemableItems } = separateCartItems();

    if (redeemableItems.length > 0 && sellingItems.length === 0) {
      setCartCompositionError(
        "Redeemable products require at least one regular product in your cart. Please add a regular product to continue."
      );
      return false;
    }

    setCartCompositionError("");
    return true;
  };

  const validateMinimumSpend = () => {
    if (minimumSpendAmount <= 0) {
      setMinimumSpendError("");
      return true;
    }

    const { sellingItems } = separateCartItems();
    const totalPrice = sellingItems.reduce((total, product) => total + getEffectivePrice(product) * product.quantity, 0);

    if (totalPrice < minimumSpendAmount) {
      setMinimumSpendError(
        `Minimum order amount is RM${minimumSpendAmount.toFixed(2)}.`
      );
      return false;
    }

    setMinimumSpendError("");
    return true;
  };

  useEffect(() => {
    const fetchCartSettings = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/cart/settings`);
        if (response.ok) {
          const data = await response.json();
          setMinimumSpendAmount(data.minimumSpendAmount || 0);
        }
      } catch (error) {
      }
    };

    fetchCartSettings();
  }, []);

  useEffect(() => {
    const fetchProductDetails = async () => {
      setIsLoading(true);
      const details = {};
      for (const item of cart) {
        // Promotion items are not catalogue products; the cart line already carries
        // everything needed to render them
        if (item.promotionProductId) continue;

        try {
          const response = await fetch(
            `${API_BASE_URL}/products/${item.productID}`
          );
          if (response.ok) {
            const data = await response.json();
            details[item.productID] = data;
          }
        } catch (error) {
        }
      }
      setProductDetails(details);
      setIsLoading(false);
    };

    fetchProductDetails();

    fetch(`${API_BASE_URL}/api/Promotions`)
      .then(r => r.ok ? r.json() : [])
      .then(promos => {
        const map = {};
        promos.forEach(p => {
          // Standalone promos are not catalogue products - they have no id to key on
          if (!p.productId && !p.variantId) return;
          map[p.variantId ? `v${p.variantId}` : `p${p.productId}`] = p.promotionPrice;
        });
        setPromoMap(map);
      })
      .catch(() => {});
  }, [cart]);

  useEffect(() => {
    if (cart.length > 0) {
      validateCartComposition();
      validateMinimumSpend();
    } else {
      setCartCompositionError("");
      setMinimumSpendError("");
    }
  }, [cart, minimumSpendAmount]);

  // Active promotion for this line, if any. A promotion may target a specific
  // variant (keyed v<variantId>) or the product's base price (keyed p<productId>).
  const getPromoFor = (product) => {
    if (product.isRedeemable) return null;
    // A promotion item already holds its promotion price - never re-map it
    if (product.promotionProductId) return null;
    const key = product.selectedVariant?.variantId
      ? `v${product.selectedVariant.variantId}`
      : `p${product.productID}`;
    return promoMap[key] ?? null;
  };

  // Returns the promo price when one is active, else the original cart price
  const getEffectivePrice = (product) => getPromoFor(product) ?? product.price;

  // MOQ helpers — fall back to fetched product details for items added before
  // moq/isIncreaseByMOQ were stored on the cart item.
  const getEffectiveMoq = (product) =>
    product.moq || productDetails[product.productID]?.moq || 1;

  const getEffectiveIncByMoq = (product) =>
    product.isIncreaseByMOQ !== undefined && product.isIncreaseByMOQ !== false
      ? product.isIncreaseByMOQ
      : (productDetails[product.productID]?.isIncreaseByMOQ || false);

  const increaseQuantity = (product) => {
    if (getEffectiveIncByMoq(product)) {
      addToCart(product, getEffectiveMoq(product));
    } else {
      addToCart(product, 1);
    }
  };

  const removeProduct = (productID, variantId, productName, promotionProductId) => {
    Swal.fire({
      title: 'Remove Item?',
      text: `Are you sure you want to remove ${productName} from your cart?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#0288d1',
      cancelButtonColor: '#7a7a7a',
      confirmButtonText: 'Yes'
    }).then((result) => {
      if (result.isConfirmed) {
        removeFromCart(productID, variantId, promotionProductId);
        Swal.fire({
          title: 'Removed!',
          text: 'The item has been removed from your cart.',
          icon: 'success',
          timer: 1500,
          showConfirmButton: false
        });
      }
    });
  };

  const handleEmptyCart = () => {
    if (cart.length === 0) {
      Swal.fire({
        title: 'Cart Empty',
        text: 'Your cart is already empty.',
        icon: 'info',
        confirmButtonColor: '#0288d1'
      });
    } else {
      Swal.fire({
        title: 'Empty Cart?',
        text: 'Are you sure you want to empty your cart? This action cannot be undone.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#0288d1',
        cancelButtonColor: '#7a7a7a',
        confirmButtonText: 'Yes'
      }).then((result) => {
        if (result.isConfirmed) {
          clearCart();
          Swal.fire({
            title: 'Success!',
            text: 'Your cart has been emptied.',
            icon: 'success',
            timer: 1500,
            showConfirmButton: false
          });
        }
      });
    }
  };

  const handleCheckout = () => {
    if (cart.length === 0) {
      Swal.fire({
        title: 'Empty Cart',
        text: 'Your cart is empty. Please add items to your cart before checking out.',
        icon: 'info',
        confirmButtonColor: '#0288d1'
      });
      return;
    }

    if (cartCompositionError) {
      Swal.fire({
        title: 'Invalid Cart Composition',
        text: cartCompositionError,
        icon: 'warning',
        confirmButtonColor: '#0288d1'
      });
      return;
    }

    if (minimumSpendError) {
      Swal.fire({
        title: 'Minimum Order Amount Not Met',
        text: minimumSpendError,
        icon: 'warning',
        confirmButtonColor: '#0288d1'
      });
      return;
    }

    if (!isLoggedIn || !userInfo) {
      Swal.fire({
        title: 'Login Required',
        text: 'You need to be logged in to place an order. Would you like to login now?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#0288d1',
        cancelButtonColor: '#7a7a7a',
        confirmButtonText: 'Login',
        cancelButtonText: 'Continue Shopping'
      }).then((result) => {
        if (result.isConfirmed) {
          navigate('/login', { 
            state: { 
              from: '/checkout'
            }
          });
        }
      });
      return;
    }

    navigate('/checkout');
  };

  const totalItems = cart.reduce((total, product) => total + product.quantity, 0);
  const { sellingItems } = separateCartItems();
  const totalPrice = sellingItems.reduce((total, product) => total + getEffectivePrice(product) * product.quantity, 0).toFixed(2);

  const hasValidationErrors = cartCompositionError || minimumSpendError;

  return (
    <div className="user-cart-container">
      <div className="user-cart-wrapper">
        <div className="user-cart">
          <div className="user-cart-title">My Shopping Cart</div>

          {/* Login status indicator */}
          {!isLoggedIn && cart.length > 0 && (
            <div className="user-cart-warning-message">
              <p>⚠️ You need to be logged in to place an order. <Link to="/login" style={{color: '#0288d1', textDecoration: 'underline'}}>Login here</Link></p>
            </div>
          )}

          {/* Cart Composition Error Message */}
          {cartCompositionError && (
            <div className="user-cart-error-message">
              <p>{cartCompositionError}</p>
            </div>
          )}

          {/* Minimum Spend Error Message */}
          {minimumSpendError && (
            <div className="user-cart-error-message">
              <p>{minimumSpendError}</p>
            </div>
          )}

          {/* Show Empty Cart Message */}
          {cart.length === 0 ? (
            <div className="user-cart-empty-message">
              <p>Your shopping cart is currently empty.</p>
              <p>Browse our products and add items to your cart.</p>
            </div>
          ) : (
            <div className="user-cart-items">
              {cart.map((product) => (
                <div key={product.promotionProductId
                  ? `promo-${product.promotionProductId}`
                  : `${product.productID}-${product.selectedVariant?.variantId || 'default'}`} className="user-cart-item">
                  {/* Remove Item From Cart */}
                  <div className="user-cart-buttons">
                    <button
                      onClick={() =>
                        removeProduct(
                          product.productID,
                          product.selectedVariant?.variantId,
                          product.name,
                          product.promotionProductId
                        )
                      }
                      className="user-cart-delete-btn"
                      aria-label="Remove item"
                    >
                      ×
                    </button>
                    {/* Only Visible in Mobile View */}
                    <button
                      onClick={() =>
                        removeProduct(
                          product.productID,
                          product.selectedVariant?.variantId,
                          product.name,
                          product.promotionProductId
                        )
                      }
                      className="user-cart-delete-btn-mobile"
                    >
                      Remove
                    </button>
                  </div>

                  {/* Image of Product  */}
                  <div className="user-cart-image">
                    <img
                      // Promotion lines carry their own image; catalogue lines look theirs up
                      src={
                        product.promotionProductId
                          ? (product.imageUrl || "./images.png")
                          : (productDetails[product.productID]?.mainImageUrl
                              || `${API_BASE_URL}/products/image/${product.productID}`)
                      }
                      alt={product.name}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "./images.png";
                      }}
                    />
                  </div>

                  {/* Product Description */}
                  <div className="user-cart-description">
                    <div className="user-cart-variant-title">
                      <span>{product.name}</span>
                    </div>
                    {product.selectedVariant && (
                      <div className="user-cart-variant-details">
                        {product.selectedVariant.variantValues?.map(
                          (variantValue) => {
                            const specValue = productDetails[
                              product.productID
                            ]?.specifications
                              ?.flatMap((s) => s.specificationValues || [])
                              .find(
                                (sv) =>
                                  sv.specificationValueId ===
                                  variantValue.specValueId
                              );
                            return specValue ? (
                              <span key={variantValue.specValueId}>
                                {
                                  productDetails[
                                    product.productID
                                  ]?.specifications?.find(
                                    (s) =>
                                      s.specificationId ===
                                      specValue.specificationId
                                  )?.name
                                }
                                : {specValue.value}
                              </span>
                            ) : null;
                          }
                        )}
                      </div>
                    )}
                  </div>

                  {/* Quantity Selection */}
                  <div className="user-cart-quantity">
                    <button
                      onClick={() => decreaseQuantity(product.productID, product.selectedVariant?.variantId, getEffectiveMoq(product), getEffectiveIncByMoq(product), product.promotionProductId)}
                      className="user-cart-minus-btn"
                      aria-label="Decrease quantity"
                      disabled={product.quantity <= getEffectiveMoq(product)}
                    >
                      -
                    </button>
                    <input
                      type="text"
                      name="quantity"
                      value={product.quantity}
                      readOnly
                      aria-label="Product quantity"
                    />
                    <button
                      onClick={() => increaseQuantity(product)}
                      className="user-cart-plus-btn"
                      aria-label="Increase quantity"
                    >
                      +
                    </button>
                  </div>

                  <div className="user-cart-total-price">
                    {product.isRedeemable ? (
                      <span style={{ color: '#0288d1', fontWeight: 'bold' }}>
                        {(productDetails[product.productID]?.pointsRequired || 0) * product.quantity} pts
                      </span>
                    ) : (
                      <>
                        <div className="user-cart-unit-price">
                          {getPromoFor(product) ? (
                            <>
                              <span style={{ textDecoration: 'line-through', color: '#aaa', marginRight: '4px' }}>
                                RM{parseFloat(product.price).toFixed(2)}
                              </span>
                              <span style={{ color: '#e53935' }}>RM{parseFloat(getPromoFor(product)).toFixed(2)}</span>
                            </>
                          ) : (
                            `RM${parseFloat(product.price).toFixed(2)}`
                          )} / {product.uom || productDetails[product.productID]?.uom || 'unit'}
                        </div>
                        <div className="user-cart-item-total">
                          RM{(getEffectivePrice(product) * product.quantity).toFixed(2)}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Summary of Cart */}
          <div className="user-cart-summary">
            <div className="user-cart-total-items">
              <span className="head">Total Items:</span>
              <span>{totalItems}</span>
            </div>
            <div className="user-cart-total-price-summary">
              <span className="head">Total Price:</span>
              <span>RM {totalPrice}</span>
            </div>

            {/* Cart Actions */}
            <div className="user-cart-actions">
              <button
                onClick={handleEmptyCart}
                className="user-cart-empty-btn"
              >
                Empty Cart
              </button>
              <button
                onClick={handleCheckout}
                className={`user-cart-checkout-btn ${hasValidationErrors || (!isLoggedIn && cart.length > 0) ? 'disabled' : ''}`}
                disabled={hasValidationErrors}
              >
                {!isLoggedIn && cart.length > 0 ? 'Login to Checkout' : 'Proceed to Checkout'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Cart;